import SelectInput, {
    SelectOption,
} from '@components/common/SelectInput/SelectInput';
import DatePicker from '@components/common/DatePicker';
import TextInput from '@components/common/TextInput';
import { farmingHist, FarmlandAddReq } from '@/api/growth/farmland/type';
import { useState } from 'react';
import { formatDateString, parseDate } from '@utils/Utils';

interface WaterMngProps {
    formData: FarmlandAddReq;
    onInputChange: (field: string, value: farmingHist[]) => void;
}

const WaterMng = ({ formData, onInputChange }: WaterMngProps) => {
    console.log(formData);
    const [farmingHists, setFarmingHists] = useState<farmingHist[]>(
        formData.farmingHistList?.filter(h => h.category === 'I') || [
            {
                category: 'I',
                workDt: '',
                iamt: '',
                iame: '',
                chcod: '',
                chamt: '',
                chme: '',
                erad: '',
                emax: '',
                emin: '',
                ewind: '',
                ename: '',
                fmcd: '',
                facd: '',
                famn: '',
                famp: '',
                famk: '',
                rcod: '',
                ramt: '',
                rdep: '',
                timpl: '',
                tdep: '',
            },
        ],
    );

    const handleFarmingHistChange = (
        index: number,
        field: keyof farmingHist,
        value: string | number,
    ) => {
        const updated = [...farmingHists];
        updated[index] = { ...updated[index], [field]: value };
        setFarmingHists(updated);
        // ✅ 부모에 즉시 업데이트 (다른 카테고리 데이터 유지)
        updateParentFarmingHistList(updated);
    };

    const updateParentFarmingHistList = (waterData: farmingHist[]) => {
        // formData의 기존 데이터 중 물관리(I)가 아닌 것들 유지
        const otherData =
            formData.farmingHistList?.filter(h => h.category !== 'I') || [];
        const allData = [...otherData, ...waterData];
        onInputChange('farmingHistList', allData);
    };

    const addFarmingHist = () => {
        const newRecord: farmingHist = {
            category: 'I',
            workDt: '',
            iamt: '',
            iame: '',
            chcod: '',
            chamt: '',
            chme: '',
            erad: '',
            emax: '',
            emin: '',
            ewind: '',
            ename: '',
            fmcd: '',
            facd: '',
            famn: '',
            famp: '',
            famk: '',
            rcod: '',
            ramt: '',
            rdep: '',
            timpl: '',
            tdep: '',
        };
        const updated = [...farmingHists, newRecord];
        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const removeFarmingHist = (index: number) => {
        const updated = farmingHists.filter((_, i) => i !== index);
        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const irrigationMethodOptions: SelectOption[] = [
        {
            value: 'IR001',
            label: '고랑 관개 (추천) - 고랑을 따라 물을 대는 방식',
        },
        {
            value: 'IR002',
            label: '교호 고랑 관개 - 고랑을 하나 건너 하나씩 번갈아 물을 대는 방식',
        },
        {
            value: 'IR003',
            label: '전면 관개 - 논처럼 토양 표면 전체를 물로 덮는 방식',
        },
        {
            value: 'IR004',
            label: '스프링클러 - 위에서 비처럼 물을 뿌리는 방식',
        },
        {
            value: 'IR005',
            label: '점적 관수 - 호스를 통해 작물 뿌리 근처에 물방울을 떨어뜨리는 방식',
        },
        {
            value: 'IR006',
            label: '담수 깊이 조절 - 논에 채워진 물의 깊이를 일정하게 유지',
        },
    ];

    return (
        <div className="water-management">
            <div className="row mb-3">
                <div className="col-12 text-end">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={addFarmingHist}
                    >
                        <i className="ri-add-line me-1"></i>
                        물관리 추가
                    </button>
                </div>
            </div>

            {farmingHists.length > 0 && (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-3">
                        <thead className="text-center">
                            <tr>
                                <th style={{ width: '30%' }}>일자 선택</th>
                                <th style={{ width: '45%' }}>관개 방법</th>
                                <th style={{ width: '15%' }}>관개량 (mm)</th>
                                <th style={{ width: '10%' }}>삭제</th>
                            </tr>
                        </thead>
                        <tbody>
                            {farmingHists.map((record, index) => (
                                <tr key={index}>
                                    <td>
                                        <DatePicker
                                            selectedDate={parseDate(
                                                record.workDt || '',
                                            )}
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'workDt',
                                                    formatDateString(value),
                                                )
                                            }
                                            placeholder="날짜를 선택해주세요"
                                            maxDate={undefined}
                                            showYearDropdown={false}
                                            showMonthDropdown={false}
                                        />
                                    </td>
                                    <td>
                                        <SelectInput
                                            options={irrigationMethodOptions}
                                            value={record.iame}
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'iame',
                                                    value,
                                                )
                                            }
                                            placeholder="선택해주세요"
                                            selectClassName="form-control"
                                        />
                                    </td>
                                    <td>
                                        <TextInput
                                            value={
                                                record.iamt
                                                    ? record.iamt.toString()
                                                    : ''
                                            }
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'iamt',
                                                    value,
                                                )
                                            }
                                            placeholder="수량 입력"
                                            type="number"
                                            numericOnly={true}
                                            allowDecimal={true}
                                        />
                                    </td>
                                    <td className="text-center">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={() =>
                                                removeFarmingHist(index)
                                            }
                                            title="삭제"
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {farmingHists.length === 0 && (
                <div className="alert alert-info text-center py-4">
                    <p className="mb-0">
                        <i className="ri-information-line me-2"></i>
                        물관리(MI) 관련 내용을 입력해주세요. 추가 버튼을 눌러
                        관개 기록을 추가할 수 있습니다.
                    </p>
                </div>
            )}
        </div>
    );
};

export default WaterMng;
